import { Component, ElementRef, ViewChild, AfterViewInit, OnDestroy, Inject, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Meta, Title } from '@angular/platform-browser';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { links } from 'src/app/shared/links';
import { RouterLink } from '@angular/router';
import { AutoplayInViewDirective } from '../../shared/directives/autoplay-in-view.directive';

@Component({
  selector: 'app-addons-page',
  standalone: true,
  imports: [RouterLink, AutoplayInViewDirective],
  templateUrl: './addons-page.component.html',
  styleUrl: './addons-page.component.scss',
})
export class AddonsPageComponent implements AfterViewInit, OnDestroy {
  @ViewChild('canvas', { static: true }) canvasRef!: ElementRef<HTMLCanvasElement>;
  @ViewChild('scrollSection', { static: true }) scrollSectionRef!: ElementRef<HTMLElement>;
  links = links;
  private ctx!: CanvasRenderingContext2D | null;
  private frames: HTMLImageElement[] = [];
  private frameCount = 123;
  private currentFrame = { index: 0 };
  private imagesLoaded = 0;
  private scrollTriggerInstance: ScrollTrigger | null = null;

  @ViewChild('video') videoExtra!: ElementRef<HTMLVideoElement>;
  private observer: IntersectionObserver
  private title = inject(Title);
  private meta = inject(Meta);
  constructor(@Inject(PLATFORM_ID) private platformId: Object) {
    // SEO (SSR): Titel + Meta-Description setzen; Ziel-Keyword „Verkaufsautomat".
    this.title.setTitle($localize`Zubehör für Verkaufsautomaten: Dach & Seitenwände – marktbox`);
    this.meta.updateTag({
      name: 'description',
      content: $localize`Machen Sie Ihren Verkaufsautomaten wetterfest: robustes Dach und Seitenwände („Hut mit Ohren") für den Außenbereich – Schutz vor Regen, Wind und Sonne.`,
    });
    if (isPlatformBrowser(this.platformId)) {
      gsap.registerPlugin(ScrollTrigger);
    }
  }

  ngAfterViewInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.initCanvas();
      this.preloadImages();

      if (this.videoExtra?.nativeElement) {
        console.log('Setting up IntersectionObserver for videoExtra');
        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              console.log('IntersectionObserver entry:', entry.isIntersecting, entry.intersectionRatio);
              if (entry.isIntersecting) {
                const video = this.videoExtra.nativeElement;
                video.muted = true;
                console.log('Attempting to play video');
                video.play().then(() => {
                  console.log('Video playing successfully');
                }).catch((err) => {
                  console.error('Error playing video:', err);
                });
                this.observer.disconnect();
              }
            });
          },
          { threshold: 0.1 }
        );
        this.observer.observe(this.videoExtra.nativeElement);
      } else {
        console.warn('Skipping IntersectionObserver setup: isBrowser or videoExtra missing');
      }
    }


  }

  ngOnDestroy(): void {
    if (this.scrollTriggerInstance) {
      this.scrollTriggerInstance.kill();
    }
    ScrollTrigger.getAll().forEach(t => t.kill());
  }

  private initCanvas(): void {
    const canvas = this.canvasRef.nativeElement;
    this.ctx = canvas.getContext('2d');

    // Set canvas size to window size for high quality rendering
    this.resizeCanvas();
    window.addEventListener('resize', () => this.resizeCanvas());
  }

  private resizeCanvas(): void {
    const canvas = this.canvasRef.nativeElement;
    canvas.width = canvas.parentElement.offsetWidth;
    canvas.height = canvas.parentElement.offsetHeight;
    this.renderFrame();
  }

  private preloadImages(): void {
    for (let i = 1; i <= this.frameCount; i++) {
      const img = new Image();
      const frameIndex = i.toString().padStart(3, '0');
      img.src = `assets/addons-page/explosion/webp2/frame-${frameIndex}.webp`;
      this.frames.push(img);
      img.onload = () => {
        this.imagesLoaded++;
        if (this.imagesLoaded === this.frameCount) {
          this.initScrollAnimation();
          this.renderFrame(); // Render first frame immediately after loading
        }
      };
      img.onerror = (e) => console.error(`Failed to load image: ${img.src}`, e);
    }
  }

  private initScrollAnimation(): void {
    // The container is the scrollable reference for the sequence duration
    this.scrollTriggerInstance = ScrollTrigger.create({
      trigger: this.scrollSectionRef.nativeElement, // This is the .addons-container
      start: 'top top',
      end: 'bottom bottom',
      scrub: 0.5,
      onUpdate: (self) => {
        const frameIndex = Math.min(
          this.frameCount - 1,
          Math.floor(self.progress * (this.frameCount - 1))
        );
        this.currentFrame.index = frameIndex;
        this.renderFrame();
      },
    });
  }

  private renderFrame(): void {
    if (!this.ctx || this.frames.length === 0) return;

    const img = this.frames[this.currentFrame.index];
    if (!img) return;

    const canvas = this.canvasRef.nativeElement;
    this.ctx.clearRect(0, 0, canvas.width, canvas.height);

    // "object-fit: cover" logic for canvas
    const hRatio = canvas.width / img.width;
    const vRatio = canvas.height / img.height;
    const ratio = Math.min(hRatio, vRatio);

    const centerShift_x = (canvas.width - img.width * ratio) / 2;
    const centerShift_y = (canvas.height - img.height * ratio) / 2;

    this.ctx.drawImage(
      img,
      0,
      0,
      img.width,
      img.height,
      centerShift_x,
      centerShift_y,
      img.width * ratio,
      img.height * ratio
    );
  }
}
